// Body-of-water flood targeting. Pure. No React, no Three.js.
//
// ── Codex (Body of Water, Flooded) ──
//   • A body of water is a contiguous group of cardinal-adjacent water
//     sites, regardless of who controls those sites.
//   • Flooded grants a site a minimum of 1 W (so a flooded site is
//     always a water site).
//   • "Adjacent" = cardinal-neighbor (4-direction).
//   • "Near" = nearby = 8-neighbor (cardinal + diagonal) + self.
//
// Two avatars use this scope:
//   • Avatar of Water. "Flood a site adjacent to your body of water."
//                       (cardinal-adjacent → adjacentFloodTargets)
//   • Waveshaper. "Flood a site near your body of water."
//                       (8-neighbor → nearbyFloodTargets)
//
// Both require the avatar to occupy a water site (else "your body of
// water" is empty and the effect fizzles). The body is computed via
// connectedSubsurfaceComponents('underwater') and matched against the
// avatar's cell.
//
// Each helper returns a Set<'col,row'> of cells that:
//   1. Are NOT inside the body itself
//   2. Contain a Site
//   3. Are within the requested adjacency to ANY body cell

import { connectedSubsurfaceComponents, isWaterSiteAtCell } from '../../../core/regions';

/**
 * Find the body-of-water component that contains the avatar's cell.
 * Returns null when the avatar isn't on a water site.
 */
export function bodyContainingCell(meshes, cellAt, totalCols, totalRows, getFullCard, cell) {
  if (!cell) return null;
  if (!isWaterSiteAtCell(meshes, cellAt, cell.col, cell.row, getFullCard)) return null;
  const components = connectedSubsurfaceComponents(
    meshes, cellAt, totalCols, totalRows, getFullCard, 'underwater',
  );
  const key = `${cell.col},${cell.row}`;
  return components.find((set) => set.has(key)) || null;
}

/**
 * Cardinal-adjacent sites to the body. Used by Avatar of Water.
 *
 * @param body Set<'col,row'> from bodyContainingCell
 * @param sitedCells Set<'col,row'> of every cell containing a Site
 * @param totalCols / totalRows grid dimensions
 * @returns Set<'col,row'> valid target cells
 */
export function adjacentFloodTargets(body, sitedCells, totalCols, totalRows) {
  return floodTargets(body, sitedCells, totalCols, totalRows, [[1, 0], [-1, 0], [0, 1], [0, -1]]);
}

/**
 * 8-neighbor sites near the body PLUS the body's own water sites. Used
 * by Waveshaper. Codex "near" = 8-neighbor + self, so the body cells
 * themselves are valid targets: Waveshaper taps minions on sites that
 * are already water, regardless of whether a flood takes place.
 */
export function nearbyFloodTargets(body, sitedCells, totalCols, totalRows) {
  const deltas = [];
  for (let dc = -1; dc <= 1; dc++) {
    for (let dr = -1; dr <= 1; dr++) {
      if (dc === 0 && dr === 0) continue;
      deltas.push([dc, dr]);
    }
  }
  const out = floodTargets(body, sitedCells, totalCols, totalRows, deltas);
  if (body) {
    for (const key of body) {
      if (sitedCells.has(key)) out.add(key);
    }
  }
  return out;
}

function floodTargets(body, sitedCells, totalCols, totalRows, deltas) {
  const out = new Set();
  if (!body) return out;
  for (const key of body) {
    const [colStr, rowStr] = key.split(',');
    const col = parseInt(colStr, 10);
    const row = parseInt(rowStr, 10);
    for (const [dc, dr] of deltas) {
      const nc = col + dc;
      const nr = row + dr;
      if (nc < 0 || nr < 0 || nc >= totalCols || nr >= totalRows) continue;
      const nKey = `${nc},${nr}`;
      if (body.has(nKey)) continue;
      if (!sitedCells.has(nKey)) continue;
      out.add(nKey);
    }
  }
  return out;
}
